import React, { useEffect } from "react";
import { Form } from "@components";
import { Wrapper } from "../../wrapper";
import { Box } from "@material-ui/core";
import { Warning as WarningIcon } from "@material-ui/icons";
import { useSalesforceFormSelect } from "@hooks";
import {
  EnvironmentSection,
  GroupQueueAssignmentSection,
  InsideSalesRepInfoSection,
  OtherPermissionsSection,
  RegionSection,
  RoleAndPersonaSection,
  SalesforceFormValues,
  SalesforceFormValuesKey,
  Case,
  Switch,
  REGIONS,
  FORM_LABELS,
  TIMEZONE_OPTIONS,
  CURRENCY_OPTIONS,
  GEOGRAPHY_OPTIONS,
  CURRENCY_OPTIONS_UK_ENTERPRISE,
} from "./details";

type Props = {
  onCancel: () => void;
  onSubmit: (values: SalesforceFormValues) => void;
  open: boolean;
  values: Partial<SalesforceFormValues>;
  readonly?: boolean;
  region: string;
  language?: "EN" | "ESP";
};

export const SalesforceForm: React.FC<Props> = ({
  onCancel,
  onSubmit,
  open,
  values,
  readonly,
  region,
  language = "EN",
}) => {
  const form = Form.useManager<SalesforceFormValues>({
    name: "",
    email: "",
    timezone: "",
    currency: "",
    geography: "",
    department: "",
    gpnIdlogin: "",
    setupType1: false,
    setupType2: false,
    setupType3: false,
    setupType4: false,
    setupType5: false,
    envQA1: false,
    envQA2: false,
    envQA3: false,
    envCert1: false,
    envCert2: false,
    envProduction: false,
    envDevelopment: false,
    regionAP: false,
    regionCA: false,
    regionUK: false,
    regionUS: false,
    envGlobal: false,
    insideFirstName: "",
    insideLastName: "",
    insidePhoneNumber: "",
    repCodeISR: "",
    repCodeUser: "",
    salesChannel: "",
    salesChannelCode: "",
    mossSalesUnit: "",
    userType: "",
    profile: "",
    role: "",
    purposeHybrid: "",
    assign1: "",
    assign2: "",
    assign3: "",
    assign4: "",
    assign5: "",
    assign6: "",
    assign7: "",
    assign8: "",
    assign9: "",
    assign10: "",
    permission1: false,
    permission2: false,
    permission3: false,
    permission4: false,
    permission5: false,
    permission6: false,
    permission7: false,
    permission8: false,
    permission9: false,
    permission10: false,
    additionalComments: "",
    region: region,
    ...(region === REGIONS.MARKETING && {
      assign11: "",
      assign12: "",
      assign13: "",
      assign14: "",
      assign15: "",
      assign16: "",
      assign17: "",
      assign18: "",
      assign19: "",
      assign20: "",
      assign21: "",
      assign22: "",
      assign23: "",
      assign24: "",
      assign25: "",
      assign26: "",
      assign27: "",
      assign28: "",
      assign29: "",
      assign30: "",
    }),
    ...values,
  });

  const {
    fetching,
    userTypeOptions,
    userProfileOptions,
    roleOptions,
  } = useSalesforceFormSelect(region, form.values.userType);

  const LABELS = FORM_LABELS[language];

  function onFormValueChange(
    name: SalesforceFormValuesKey,
    value: SalesforceFormValues[typeof name]
  ): void {
    switch (name) {
      case "setupType1":
      case "setupType2":
      case "setupType3":
      case "setupType4":
      case "setupType5":
        form.set(
          Form.target.VALUES,
          Object.assign(
            {
              setupType1: false,
              setupType2: false,
              setupType3: false,
              setupType4: false,
              setupType5: false,
            },
            {
              [name]: value,
            }
          )
        );
        break;
      case "userType":
        if (value === "Sales Profile with CSP Access") {
          form.set(Form.target.VALUE, ["permission2", true]);
        } else {
          form.set(Form.target.VALUE, ["permission2", false]);
          form.set(Form.target.VALUE, ["purposeHybrid", ""]);
        }
        if (value === "Docusign Access Only") {
          form.set(Form.target.VALUE, ["role", ""]);
        }
        form.set(Form.target.VALUE, ["profile", ""]);
        form.set(Form.target.VALUE, [
          name,
          value as SalesforceFormValues[typeof name],
        ]);
        break;
      default:
        form.set(Form.target.VALUE, [name, value]);
        break;
    }
    validate(name, value).then(([name, error]) => {
      form.set(Form.target.ERROR, [name, error]);
    });
  }

  function validate(
    name: SalesforceFormValuesKey,
    value: SalesforceFormValues[typeof name]
  ): Promise<[SalesforceFormValuesKey, Error?]> {
    switch (name) {
      case "purposeHybrid":
        return Promise.resolve([
          name as SalesforceFormValuesKey,
          form.values.userType !== "Sales Profile with CSP Access"
            ? undefined
            : Form.Validators.hasValue(value),
        ]);
      case "profile":
      case "role":
        return Promise.resolve([
          name as SalesforceFormValuesKey,
          form.values.userType === "Docusign Access Only"
            ? undefined
            : Form.Validators.hasValue(value),
        ]);
      case "name":
      case "email":
      case "department":
      case "gpnIdlogin":
      case "userType":
      case "geography":
      case "timezone":
      case "currency":
        return Promise.resolve([
          name as SalesforceFormValuesKey,
          Form.Validators.hasValue(value),
        ]);
      default:
        return Promise.resolve([name as SalesforceFormValuesKey]);
    }
  }

  function onFormSubmit(): void {
    Promise.all(
      Object.entries(form.values).map(([name, value]) =>
        validate(name as SalesforceFormValuesKey, value)
      )
    ).then((validations) => {
      const valid = validations.reduce((valid, [name, error]) => {
        return valid && !error;
      }, true);
      if (valid) {
        onSubmit(form.values);
      } else {
        validations.forEach(([name, error]) => {
          form.set(Form.target.ERROR, [name, error]);
        });
      }
    });
  }

  return (
    <Wrapper
      onCancel={() => {
        onCancel();
        if (values) {
          form.set(Form.target.VALUES, values);
        } else {
          form.reset();
        }
      }}
      onSubmit={onFormSubmit}
      open={open}
      size="md"
      title={`Salesforce ${region} Region User Access Forms For Internal User`}
      readonly={readonly}
    >
      <Form>
        <Form.Text color="warning.main">
          <Box display="flex">
            <WarningIcon />
            <Box pl={1} pt={0.5}>
              <Box pt={1}>{LABELS.FORM_TITLE_NOTE}</Box>
            </Box>
          </Box>
        </Form.Text>
        <Form.Title>{`1 - ${LABELS.USER_INFORMATION.FORM_TITLE}`}</Form.Title>
        <Form.Textbox
          error={form.errors.gpnIdlogin}
          label={LABELS.USER_INFORMATION.GPN_ID}
          name="gpnIdlogin"
          onChange={onFormValueChange}
          required
          size={6}
          value={form.values.gpnIdlogin}
        />
        <Form.Dropdown
          label={LABELS.USER_INFORMATION.GEOGRAPHY}
          onChange={onFormValueChange}
          options={GEOGRAPHY_OPTIONS[region]}
          name="geography"
          size={6}
          value={form.values.geography || ""}
          error={form.errors.geography}
          required
        />
        <Form.Textbox
          error={form.errors.name}
          label={
            region === REGIONS.AP ? "Job Title" : LABELS.USER_INFORMATION.NAME
          }
          name="name"
          onChange={onFormValueChange}
          required
          size={6}
          value={form.values.name}
        />
        <Form.Dropdown
          label={LABELS.USER_INFORMATION.TIMEZONE}
          onChange={onFormValueChange}
          options={TIMEZONE_OPTIONS}
          name="timezone"
          size={6}
          value={form.values.timezone || ""}
          error={form.errors.timezone}
          required
        />
        <Form.Textbox
          error={form.errors.email}
          label={
            region === REGIONS.AP ? "Manager" : LABELS.USER_INFORMATION.EMAIL
          }
          name="email"
          onChange={onFormValueChange}
          required
          size={6}
          value={form.values.email}
        />
        <Form.Dropdown
          label={LABELS.USER_INFORMATION.CURRENCY}
          onChange={onFormValueChange}
          options={
            region === REGIONS.UK_ENTERPRISE
              ? CURRENCY_OPTIONS_UK_ENTERPRISE
              : CURRENCY_OPTIONS
          }
          name="currency"
          size={6}
          value={form.values.currency || ""}
          error={form.errors.currency}
          required
        />
        <Form.Textbox
          error={form.errors.department}
          label={LABELS.USER_INFORMATION.DEPARTMENT}
          name="department"
          onChange={onFormValueChange}
          required
          size={6}
          value={form.values.department}
        />
        <Form.Title>{`2 - ${LABELS.SETUP_TYPE.FORM_TITLE}`}</Form.Title>
        <Form.Radiobutton
          label={LABELS.SETUP_TYPE.NEW_USER}
          name="setupType1"
          onChange={onFormValueChange}
          size={4}
          value={form.values.setupType1}
        />
        <Form.Radiobutton
          label={LABELS.SETUP_TYPE.REDUCING}
          name="setupType2"
          onChange={onFormValueChange}
          size={4}
          value={form.values.setupType2}
        />
        <Form.Radiobutton
          label={LABELS.SETUP_TYPE.ADDITIONAL}
          name="setupType3"
          onChange={onFormValueChange}
          size={4}
          value={form.values.setupType3}
        />
        <Form.Radiobutton
          label={LABELS.SETUP_TYPE.DEACTIVATE}
          name="setupType4"
          onChange={onFormValueChange}
          size={4}
          value={form.values.setupType4}
        />
        <Form.Radiobutton
          label={LABELS.SETUP_TYPE.REACTIVATE}
          name="setupType5"
          onChange={onFormValueChange}
          size={4}
          value={form.values.setupType5}
        />
        <Switch value={region}>
          <Case
            values={[REGIONS.AP]}
            component={
              <RoleAndPersonaSection
                form={form}
                onFormValueChange={onFormValueChange}
                sectionNumber={3}
                userTypeOptions={userTypeOptions}
                userProfileOptions={userProfileOptions}
                roleOptions={roleOptions}
                fetching={fetching}
                region={region}
              />
            }
          />
          <Case
            values={[
              REGIONS.UK,
              REGIONS.CANADA,
              REGIONS.MNC,
              REGIONS.CSP,
              REGIONS.CSP_LATAM,
              REGIONS.MARKETING,
              REGIONS.MEXICO,
              REGIONS.UK_ENTERPRISE,
            ]}
            component={
              <EnvironmentSection
                form={form}
                onFormValueChange={onFormValueChange}
                sectionNumber={3}
                language={language}
              />
            }
          />
        </Switch>
        <Switch value={region}>
          <Case
            values={[REGIONS.CSP]}
            component={
              <RegionSection
                form={form}
                onFormValueChange={onFormValueChange}
                sectionNumber={4}
              />
            }
          />
          <Case
            values={[REGIONS.CSP_LATAM]}
            component={
              <RegionSection
                form={form}
                onFormValueChange={onFormValueChange}
                sectionNumber={4}
              />
            }
          />
          <Case
            values={[REGIONS.UK, REGIONS.CANADA, REGIONS.MEXICO]}
            component={
              <InsideSalesRepInfoSection
                form={form}
                onFormValueChange={onFormValueChange}
                sectionNumber={4}
                language={language}
                region={region}
              />
            }
          />
          <Case
            values={[REGIONS.MARKETING, REGIONS.UK_ENTERPRISE, REGIONS.MNC]}
            component={
              <RoleAndPersonaSection
                form={form}
                onFormValueChange={onFormValueChange}
                sectionNumber={4}
                userTypeOptions={userTypeOptions}
                userProfileOptions={userProfileOptions}
                roleOptions={roleOptions}
                fetching={fetching}
                region={region}
              />
            }
          />
        </Switch>
        <Switch value={region}>
          <Case
            values={[
              REGIONS.UK,
              REGIONS.CANADA,
              REGIONS.CSP,
              REGIONS.CSP_LATAM,
              REGIONS.MEXICO,
            ]}
            component={
              <RoleAndPersonaSection
                form={form}
                onFormValueChange={onFormValueChange}
                sectionNumber={5}
                userTypeOptions={userTypeOptions}
                userProfileOptions={userProfileOptions}
                roleOptions={roleOptions}
                fetching={fetching}
                language={language}
                region={region}
              />
            }
          />
          <Case
            values={[REGIONS.MNC, REGIONS.MARKETING, REGIONS.UK_ENTERPRISE]}
            component={
              <GroupQueueAssignmentSection
                form={form}
                onFormValueChange={onFormValueChange}
                sectionNumber={5}
                extended={region === REGIONS.MARKETING}
              />
            }
          />
        </Switch>
        <Switch value={region}>
          <Case
            values={[
              REGIONS.UK,
              REGIONS.CANADA,
              REGIONS.CSP,
              REGIONS.CSP_LATAM,
              REGIONS.MEXICO,
            ]}
            component={
              <GroupQueueAssignmentSection
                form={form}
                onFormValueChange={onFormValueChange}
                sectionNumber={6}
                language={language}
              />
            }
          />
          <Case
            values={[REGIONS.MARKETING, REGIONS.UK_ENTERPRISE]}
            component={
              <OtherPermissionsSection
                form={form}
                onFormValueChange={onFormValueChange}
                sectionNumber={6}
                region={region}
              />
            }
          />
        </Switch>
        {[REGIONS.UK, REGIONS.CANADA, REGIONS.CSP, REGIONS.CSP_LATAM].includes(
          region
        ) ? (
          <OtherPermissionsSection
            form={form}
            onFormValueChange={onFormValueChange}
            sectionNumber={7}
            region={region}
          />
        ) : null}

        {[
          REGIONS.AP,
          REGIONS.MARKETING,
          REGIONS.MEXICO,
          REGIONS.MNC,
          REGIONS.UK_ENTERPRISE,
          REGIONS.UK,
          REGIONS.CANADA,
          REGIONS.CSP,
          REGIONS.CSP_LATAM,
        ].includes(region) ? (
          <Form.Textarea
            label={`${LABELS.ADDITIONAL_COMMENTS.LABEL}`}
            name="additionalComments"
            onChange={onFormValueChange}
            size={12}
            maxLength={1000}
            rows={5}
            rowsMax={5}
            value={form.values.additionalComments}
          />
        ) : null}
      </Form>
    </Wrapper>
  );
};
